import { z } from "zod";

/**
 * Usage & cost dashboard (WP-3.7, ADR-0025): aggregates over `llm_calls` and the request lifecycle.
 * Never per person: no user or request ids, names, titles or content in the report.
 */

export const USAGE_PERIODS = [7, 30, 90] as const;
export type UsagePeriod = (typeof USAGE_PERIODS)[number];

export const usageQuerySchema = z.object({
  days: z.coerce
    .number()
    .refine((d): d is UsagePeriod => (USAGE_PERIODS as readonly number[]).includes(d), "Use 7, 30 or 90.")
    .default(30),
});
export type UsageQuery = z.infer<typeof usageQuerySchema>;

/** Conversation-turn budgets in milliseconds (ARCHITECTURE §13). */
export const TURN_BUDGETS = {
  firstText: { p50: 1500, p95: 3000 },
  fullTurn: { p50: 6000, p95: 12000 },
} as const;

/** An amount in millionths of `currency` (no conversion between currencies). */
const money = z.object({ currency: z.string(), micros: z.number() });
export type Money = z.infer<typeof money>;

const percentiles = z.object({ p50: z.number().nullable(), p95: z.number().nullable() });
export type Percentiles = z.infer<typeof percentiles>;

const tokens = z.object({ input: z.number(), cachedInput: z.number(), output: z.number() });

const funnelCounts = z.object({
  started: z.number(),
  submitted: z.number(),
  reachedReview: z.number(),
  decided: z.number(),
  approved: z.number(),
  rejected: z.number(),
  abandoned: z.number(),
  /** Still a draft or returned to the requester. */
  open: z.number(),
});
export type FunnelCounts = z.infer<typeof funnelCounts>;

export const usageReportSchema = z.object({
  /** Whole UTC days: `from` is the first day, `to` today. */
  period: z.object({ days: z.number(), from: z.string(), to: z.string() }),
  totals: z.object({
    calls: z.number(),
    failed: z.number(),
    /** Attempts on a fallback model (attempt > 0). */
    fallbacks: z.number(),
    tokens,
    costs: z.array(money),
  }),
  /** Every day of the period, oldest first, zero days included. */
  daily: z.array(z.object({ date: z.string(), calls: z.number(), costs: z.array(money) })),
  byTask: z.array(
    z.object({
      task: z.string(),
      calls: z.number(),
      failed: z.number(),
      tokens,
      costs: z.array(money),
      latencyMs: percentiles,
    }),
  ),
  byModel: z.array(
    z.object({
      modelAlias: z.string(),
      provider: z.string(),
      calls: z.number(),
      failed: z.number(),
      costs: z.array(money),
    }),
  ),
  errors: z.array(z.object({ task: z.string(), code: z.string(), count: z.number() })),
  turns: z.object({
    count: z.number(),
    firstTextMs: percentiles,
    fullTurnMs: percentiles,
    costPerTurn: z.array(money),
    /** Share of input tokens served from the provider's cache (0–1); null without turns. */
    cachedShare: z.number().nullable(),
  }),
  /** Calls linked to no request and no user (evals, tests, maintenance). */
  unlinked: z.object({ calls: z.number(), costs: z.array(money) }),
  funnel: z.object({
    overall: funnelCounts,
    medianHoursToSubmit: z.number().nullable(),
    medianHoursToDecision: z.number().nullable(),
    byLegalEntity: z.array(
      funnelCounts.extend({ legalEntity: z.object({ code: z.string(), name: z.string() }).nullable() }),
    ),
  }),
});
export type UsageReport = z.infer<typeof usageReportSchema>;

/** Whether a measured percentile meets its budget; null when there is nothing measured. */
export function withinBudget(measured: number | null, budget: number): boolean | null {
  return measured === null ? null : measured <= budget;
}
